import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { ascend, prop, sortWith } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { generateApiErrorPayload } from 'api-client/helpers'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import OptionsToggler from 'core/components/OptionsToggler'
import PaginatedSelectableCards from 'core/components/PaginatedSelectableCards'
import SimpleLink from 'core/components/SimpleLink'
import PicklistField from 'core/components/validatedForm/DropdownField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import { useIsoMountProgress } from 'core/context/isoMountProgressContext'
import Dropdown from 'core/elements/dropdown'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { humanReadableSize } from 'openstack/helpers'
import { durationBetweenDates } from 'utils/misc'
import { listImages } from '../images/actions'
import { ImagePageTabs } from '../images/model'
import { imagesSelector } from '../images/selectors'
import VolumeTypesPicklist from '../storage/volume-types/VolumeTypesPicklist'
import { mountISOAction } from './actions'
import { isVolumeBackedVm } from './helpers'
import { dispatchVmIsoOperationCompleted, VmIsoOperationType } from './isoMountEvents'
import { ACTION_LIMITATION_TOOLTIPS } from './tooltipConstants'

const renderImageAge = (createdAt?: string) =>
  createdAt ? durationBetweenDates({ labels: ['d'] })(createdAt) : 'N/A'

const isoImageColumns = [
  { id: 'name', label: 'Name' },
  { id: 'id', label: 'UUID', display: false },
  { id: 'disk_format', label: 'Format' },
  {
    id: 'virtual_size',
    label: 'Virtual Size',
    render: humanReadableSize,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (value) => renderImageAge(value),
  },
]

const visibleIsoImageColumns = ['name', 'disk_format', 'virtual_size', 'created_at']

const viewToggleOptions = [
  { icon: 'list', value: 'list' },
  { icon: 'grid', value: 'grid' },
]

const sortOptions = [
  { label: 'Select...', value: '' },
  { label: 'Name', value: 'name' },
  { label: 'Size', value: 'virtualSize' },
]

const resolveIsoImageSizeBytes = (image: any): number | undefined => {
  const parsedVirtualSize = Number(image?.virtual_size)
  if (Number.isFinite(parsedVirtualSize) && parsedVirtualSize > 0) {
    return parsedVirtualSize
  }

  const parsedSize = Number(image?.size)
  if (Number.isFinite(parsedSize) && parsedSize > 0) {
    return parsedSize
  }

  return undefined
}

export default function MountISODialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const [error, setError] = useState(null)
  const [selectedImage, setSelectedImage] = useState<any>(null)
  const [viewToggle, setViewToggle] = useState('grid')
  const [isoSort, setIsoSort] = useState('')
  const { addOperation, updateOperation } = useIsoMountProgress()

  const defaultParams = { image: [], volumeType: '' }
  const { params, getParamsUpdater } = useParams(defaultParams)

  const { loading: loadingImages } = useListAction(listImages, { params: {} })
  const images = useSelectorWithParams(imagesSelector, {} as any)

  const isoImages = useMemo(
    () => images.filter((image) => image.status === 'active' && image.disk_format === 'iso'),
    [images],
  )

  const isoSorters = {
    name: sortWith([ascend(prop('name'))]),
    virtualSize: sortWith([ascend(prop('virtual_size'))]),
  }

  const sortedIsoImages = useMemo(
    () => (isoSort && isoSorters[isoSort] ? isoSorters[isoSort](isoImages) : isoImages),
    [isoImages, isoSort],
  )

  const handleSubmit = useCallback(() => {
    if (!vm?.id) {
      setError({ title: '', message: 'Unable to identify VM for ISO mount' })
      return
    }

    const image = viewToggle === 'grid' ? selectedImage : params.image?.[0]
    if (!image) {
      setError({ title: '', message: 'Please select an ISO image' })
      return
    }

    setError(null)
    const operationId = `iso-mount-${vm.id}-${Date.now()}`

    addOperation({
      id: operationId,
      vmId: vm.id,
      vmName: vm.name,
      isoImageId: image.id,
      isoImageName: image.name,
      status: 'in_progress',
      error: null,
      createdAt: Date.now(),
    })

    // Close immediately and let background operation continue.
    onClose(false)

    void mountISOAction({
      vmId: vm.id,
      isoImageId: image.id,
      vmName: vm?.name,
      isoImageSizeBytes: resolveIsoImageSizeBytes(image),
      volumeType: params.volumeType,
    })
      .then((result) => {
        updateOperation(operationId, { status: 'completed', error: null })
        dispatchVmIsoOperationCompleted({
          vmId: vm.id,
          operation: VmIsoOperationType.Mount,
          volumeId: result?.volumeId,
        })
      })
      .catch((err) => {
        const message =
          generateApiErrorPayload(err, '', 'Failed to mount ISO')?.message || 'Failed to mount ISO'
        updateOperation(operationId, {
          status: 'failed',
          error: message,
          failedVolumeId: err?.failedVolumeId ?? null,
        })
      })
  }, [selectedImage, params, vm, viewToggle, addOperation, updateOperation, onClose])

  const imagesPath = routeManager.routes.openstack.images.path({ tab: ImagePageTabs.Images })

  return (
    <ModalForm
      open
      panel="drawer"
      title={`Mount ISOs to VM (${vm?.name || vm?.id})`}
      onSubmit={handleSubmit}
      onClose={onClose}
      loading={loadingImages}
      error={error}
      submitTitle="Mount"
    >
      <div className={classes.fields}>
        <div className={classes.description}>
          <FontAwesomeIcon className={classes.descIcon}>mount-iso</FontAwesomeIcon>
          <Text variant="body2" className={classes.descText}>
            The ISO will be attached as a virtual CD/DVD drive.
            <br />
            You can then mount and access its contents from within the VM's operating system.
          </Text>
        </div>

        {/* FR MID-FR-001/MID-FR-002: volume type for the ISO backing volume, defaulting
            to the tenant's default type (mirrors the Create Volume flow). */}
        <PicklistField
          DropdownComponent={VolumeTypesPicklist}
          id="volumeType"
          onChange={getParamsUpdater('volumeType')}
          value={params.volumeType}
          label="Volume Type"
          required
        />

        <Text variant="subtitle2" className={classes.sectionLabel}>
          Select ISO Image(s)
        </Text>

        {!loadingImages && isoImages.length === 0 ? (
          <div className={classes.emptyState}>
            <Text variant="body2">
              No ISO images found.{' '}
              <SimpleLink src={imagesPath}>Upload one in the Images section.</SimpleLink>
            </Text>
          </div>
        ) : (
          <>
            {viewToggle === 'grid' && (
              <PaginatedSelectableCards
                data={sortedIsoImages}
                searchProps={['name', 'disk_format']}
                numColumns={3}
                maxRows={4}
                pluralLabel="ISO images"
                extraHeaderContent={
                  <OptionsToggler
                    options={viewToggleOptions}
                    value={viewToggle}
                    onChange={setViewToggle}
                  />
                }
                sortDropdown={
                  <Dropdown
                    name="sort"
                    value={isoSort}
                    items={sortOptions}
                    onChange={(value) => setIsoSort(value)}
                  />
                }
                renderSelectableCardComponent={(image) => {
                  const isSelected = selectedImage?.id === image.id
                  return (
                    <div
                      key={image.id}
                      className={clsx(classes.card, { [classes.cardSelected]: isSelected })}
                      onClick={() => setSelectedImage(isSelected ? null : image)}
                    >
                      <div
                        className={clsx(classes.cardRadio, {
                          [classes.cardRadioSelected]: isSelected,
                        })}
                      >
                        {isSelected && <div className={classes.cardRadioDot} />}
                      </div>
                      <div className={classes.cardContent}>
                        <div className={classes.cardNameRow}>
                          <Text variant="body2" className={classes.cardName}>
                            {image.name || image.id}
                          </Text>
                        </div>
                        <div className={classes.cardMeta}>
                          <Text variant="body2" className={classes.cardMetaLine}>
                            {'Format: '}
                            <span className={classes.cardMetaBold}>{image.disk_format}</span>
                          </Text>
                          <Text variant="body2" className={classes.cardMetaLine}>
                            {'Virtual Size: '}
                            <span className={classes.cardMetaBold}>
                              {humanReadableSize(image.virtual_size)}
                            </span>
                          </Text>
                          <Text variant="body2" className={classes.cardMetaLine}>
                            {'Age: '}
                            <span className={classes.cardMetaBold}>
                              {renderImageAge(image.created_at)}
                            </span>
                          </Text>
                        </div>
                      </div>
                    </div>
                  )
                }}
              />
            )}
            {viewToggle === 'list' && (
              <>
                <div className={classes.listToggleRow}>
                  <OptionsToggler
                    options={viewToggleOptions}
                    value={viewToggle}
                    onChange={setViewToggle}
                  />
                </div>
                <ListTableField
                  id="image"
                  data={sortedIsoImages}
                  loading={loadingImages}
                  columns={isoImageColumns}
                  visibleColumns={visibleIsoImageColumns}
                  onChange={getParamsUpdater('image')}
                  value={params.image}
                  uniqueIdentifier="id"
                  searchTargets={['name', 'disk_format']}
                />
              </>
            )}
          </>
        )}

        <div className={classes.footer}>
          <FontAwesomeIcon className={classes.footerIcon}>arrow-up-from-bracket</FontAwesomeIcon>
          <Text variant="body2">
            Need a new ISO? Upload one in the{' '}
            <SimpleLink src={imagesPath}>Images section.</SimpleLink>
          </Text>
        </div>
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  description: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },
  descIcon: {
    fontSize: 24,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  descText: {
    lineHeight: 1.6,
  },
  sectionLabel: {
    fontWeight: 600,
  },
  listToggleRow: {
    marginBottom: theme.spacing(1),
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: '16px 20px',
    border: `1px solid #e6e6ea`,
    borderRadius: 4,
    cursor: 'pointer',
    background: theme.palette.common.white,
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  cardRadio: {
    width: 16,
    height: 16,
    flexShrink: 0,
    border: '1px solid #b6b6c0',
    borderRadius: '50%',
    background: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardRadioSelected: {
    borderColor: theme.palette.primary.main,
  },
  cardRadioDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
  cardNameRow: {
    borderBottom: '1px solid #e6e6ea',
    paddingBottom: 8,
    width: '100%',
  },
  cardName: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '20px',
    color: '#3d3d57',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  cardMeta: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardMetaLine: {
    fontSize: 12,
    lineHeight: '20px',
    color: '#3d3d57',
    whiteSpace: 'nowrap',
  },
  cardMetaBold: {
    fontWeight: 600,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    border: `1px dashed ${theme.palette.grey[300]}`,
    borderRadius: 4,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  footerIcon: {
    fontSize: 16,
    flexShrink: 0,
  },
}))
